import { useState } from 'react';
import { BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { timeAgo } from '../quality/qualityConfig';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const errorText = (error, fallback) => error.response?.data?.detail || fallback;
const hoursUntil = (iso) => Math.max(1, Math.ceil((new Date(iso) - Date.now()) / 3600000));

export const EEOCNudgeAllButton = ({ summary, department, onDone }) => {
    const { api } = useAuth();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const count = summary.nudgeable || 0;

    const send = async (event) => {
        event.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post('/eeoc-training/admin/nudge-overdue', { department: department || null });
            toast.success(`Sent ${plural(data.nudged, 'reminder')}`, { description: data.skipped ? `${data.skipped} skipped (already reminded in the last 24 hours)` : undefined });
            setOpen(false);
            onDone();
        } catch (error) {
            toast.error(errorText(error, 'Could not send reminders'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button size="sm" disabled={!count} onClick={() => setOpen(true)} className="bg-red-600 text-white hover:bg-red-700"
                title={!count && summary.overdue ? 'Everyone overdue was already reminded in the last 24 hours' : undefined} data-testid="eeoc-nudge-all-btn">
                <BellRing className="mr-1.5 h-4 w-4" /> Nudge all overdue ({count})
            </Button>
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent data-testid="eeoc-nudge-all-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Send {plural(count, 'reminder')}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Everyone past their EEOC due date{department ? ` in ${department}` : ''} who hasn't been reminded in the last 24 hours will see a pop-up and a red EEOC banner. In-app only, no email.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="eeoc-nudge-all-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={send} disabled={busy} className="bg-red-600 text-white hover:bg-red-700" data-testid="eeoc-nudge-all-confirm">
                            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send reminders
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export const EEOCNudgeCell = ({ row, index, isCurrent, onDone }) => {
    const { api } = useAuth();
    const [busy, setBusy] = useState(false);
    if (!isCurrent || (!row.overdue && !row.nudge_count)) return <span className="text-muted-foreground">-</span>;

    const nudge = async () => {
        setBusy(true);
        try {
            await api.post(`/eeoc-training/admin/users/${row.user_id}/nudge`);
            toast.success(`Reminder sent to ${row.user_name || row.email}`);
            onDone();
        } catch (error) {
            toast.error(errorText(error, 'Could not send the reminder'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-1">
            {row.overdue && (
                <Button size="sm" variant="outline" disabled={busy || !row.can_nudge} onClick={nudge}
                    className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40" data-testid={`eeoc-nudge-btn-${index}`}>
                    {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <BellRing className="mr-1 h-3.5 w-3.5" />}
                    {row.can_nudge ? 'Nudge' : `Next in ${hoursUntil(row.next_nudge_at)}h`}
                </Button>
            )}
            {row.nudge_count > 0 && (
                <p className="text-xs text-muted-foreground" data-testid={`eeoc-nudge-info-${index}`}>
                    {plural(row.nudge_count, 'reminder')} · last {timeAgo(row.last_nudged_at)}
                </p>
            )}
        </div>
    );
};
